import { Link } from "react-router-dom";
import { useSeo, SITE } from "@/lib/seo";

// Public, crawlable catalogue. Copy: free-to-play Fun Coins only — no real-money or winnings wording.
export const GAME_CATALOG = [
  { slug: "daily-challenges", name: "Daily Challenges", kind: "Skill", genre: "Puzzle",
    desc: "A fresh WordVolt letter rack and a shared Solitaire deal every day. Everyone gets the same puzzle — compare your score on the daily leaderboard.",
    alt: "PhonePlay Daily Challenges tile — a new WordVolt and Solitaire puzzle every day" },
  { slug: "wordvolt", name: "WordVolt", kind: "Skill", genre: "Word",
    desc: "Build the highest-scoring words from a shared rack of letters in 60 seconds. Vocabulary and speed decide your score.",
    alt: "PhonePlay WordVolt tile — a 60-second word-building skill game" },
  { slug: "speed-math", name: "Speed Math", kind: "Skill", genre: "Educational",
    desc: "Solve as many arithmetic problems as you can before the clock runs out. Fast, accurate minds climb the ranks.",
    alt: "PhonePlay Speed Math tile — timed arithmetic skill game" },
  { slug: "solitaire-sprint", name: "Solitaire Sprint", kind: "Skill", genre: "Card",
    desc: "Same deal for every player — clear the longest card chain you can in 60 seconds.",
    alt: "PhonePlay Solitaire Sprint tile — fast solitaire skill game" },
  { slug: "reaction-rush", name: "Reaction Rush", kind: "Skill", genre: "Action",
    desc: "Tap the target the instant it lights up. Pure reflexes, scored on our servers.",
    alt: "PhonePlay Reaction Rush tile — reflex-testing tap game" },
  { slug: "memory-matrix", name: "Memory Matrix", kind: "Skill", genre: "Puzzle",
    desc: "Memorise the pattern, then repeat it. Each round the grid grows — how far can your memory go?",
    alt: "PhonePlay Memory Matrix tile — pattern memory puzzle game" },
  { slug: "precision-wheel", name: "Precision Wheel", kind: "Skill arcade", genre: "Arcade",
    desc: "Stop the spinning pointer on the target zone. Timing is everything in this free practice arcade game.",
    alt: "PhonePlay Precision Wheel tile — timing-based arcade skill game" },
  { slug: "card-21", name: "Card 21", kind: "Skill arcade", genre: "Card",
    desc: "A skill twist on twenty-one: every player sees the same shuffled deck, so decisions — not luck — set your score.",
    alt: "PhonePlay Card 21 tile — skill-based card game with a shared deck" },
  { slug: "skill-reels", name: "Skill Reels", kind: "Skill arcade", genre: "Arcade",
    desc: "Stop each reel yourself to line up symbols. A timing challenge, not a slot machine — free practice only.",
    alt: "PhonePlay Skill Reels tile — stop-the-reel timing game" },
  { slug: "neon-slots", name: "Neon Slots", kind: "Fun Coin arcade", genre: "Casual",
    desc: "A 3-reel arcade spinner played with free Fun Coins. Provably-fair results, coins have no monetary value.",
    alt: "PhonePlay Neon Slots tile — 3-reel Fun Coin arcade spinner" },
  { slug: "crash", name: "Crash", kind: "Fun Coin arcade", genre: "Casual",
    desc: "Watch the multiplier climb and lock in your Fun Coins before it busts. Provably fair, just for fun.",
    alt: "PhonePlay Crash tile — rising multiplier Fun Coin arcade game" },
  { slug: "live-crash", name: "Live Crash", kind: "Fun Coin arcade", genre: "Casual",
    desc: "The shared, real-time version of Crash: every player watches the same round together. Real players only, no bots.",
    alt: "PhonePlay Live Crash tile — shared real-time multiplier arcade round" },
  { slug: "plinko", name: "Plinko", kind: "Fun Coin arcade", genre: "Casual",
    desc: "Drop a chip through the pegs and see which multiplier slot it lands in. Every result is exactly the number shown.",
    alt: "PhonePlay Plinko tile — chip-drop peg board arcade game" },
  { slug: "fortune-wheel", name: "Fortune Wheel", kind: "Fun Coin arcade", genre: "Casual",
    desc: "Spin the wheel for Fun Coin multipliers. A quick, colourful arcade spin with no monetary value.",
    alt: "PhonePlay Fortune Wheel tile — spinning multiplier wheel arcade game" },
  { slug: "mines", name: "Mines", kind: "Fun Coin arcade", genre: "Casual",
    desc: "Reveal gems, dodge the bombs and bank your Fun Coins whenever you like.",
    alt: "PhonePlay Mines tile — gem-and-bomb grid arcade game" },
  { slug: "crash-mines", name: "Crash Mines", kind: "Fun Coin arcade", genre: "Casual",
    desc: "Two arcade classics fused: a mines grid where every safe gem also raises a crash multiplier.",
    alt: "PhonePlay Crash Mines tile — hybrid mines and crash arcade game" },
  { slug: "wheel-slots", name: "Wheel Slots", kind: "Fun Coin arcade", genre: "Casual",
    desc: "Reels spin, then a bonus wheel multiplies the result. Fun Coins only, exact displayed results.",
    alt: "PhonePlay Wheel Slots tile — reels plus bonus wheel arcade game" },
  { slug: "blackjack", name: "Blackjack", kind: "Fun Coin tables", genre: "Card",
    desc: "Classic twenty-one against the dealer, played with free Fun Coins at a social table.",
    alt: "PhonePlay Blackjack tile — social Fun Coin card table" },
  { slug: "roulette", name: "Roulette", kind: "Fun Coin tables", genre: "Casual",
    desc: "Red, black or your number — a social roulette table played with Fun Coins for fun only.",
    alt: "PhonePlay Roulette tile — social Fun Coin roulette table" },
  { slug: "baccarat", name: "Baccarat", kind: "Fun Coin tables", genre: "Card",
    desc: "Player, banker or tie. A quick social card table, Fun Coins only.",
    alt: "PhonePlay Baccarat tile — social Fun Coin baccarat table" },
];

const GROUPS = ["Skill", "Skill arcade", "Fun Coin arcade", "Fun Coin tables"];

export default function Games() {
  useSeo({
    title: "All Games — Free Skill Games, Daily Challenges & Fun Coin Arcade",
    description: "Browse every PhonePlay game: WordVolt, Speed Math, Solitaire Sprint, Reaction Rush, Memory Matrix, Plinko, Crash, Neon Slots and more. Free to play with Fun Coins — coins have no monetary value.",
    path: "/games",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "PhonePlay games",
      "itemListElement": GAME_CATALOG.map((g, i) => ({
        "@type": "ListItem", "position": i + 1,
        "item": {
          "@type": "VideoGame", "name": g.name, "description": g.desc, "genre": g.genre,
          "url": `${SITE}/games#${g.slug}`, "image": `${SITE}/games/${g.slug}.webp`,
          "gamePlatform": ["Web browser", "Mobile web"], "applicationCategory": "Game", "operatingSystem": "Any",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "description": "Free to play with Fun Coins. Coins have no monetary value." },
          "publisher": { "@type": "Organization", "name": "Phone Play LLC" },
        },
      })),
    },
  });

  return (
    <article data-testid="games-page">
      <h1 className="font-display text-3xl sm:text-4xl font-black mb-3">All Games</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Every game on PhonePlay is free to play with Fun Coins. Skill games are scored on our servers and ranked on
        leaderboards; arcade games are provably fair and pay exactly the multiplier shown. 18+ · Coins have no monetary
        value · No real-money gambling.
      </p>
      {GROUPS.map((group) => (
        <section key={group} className="mb-10" aria-labelledby={`group-${group.replace(/\s+/g, "-")}`}>
          <h2 id={`group-${group.replace(/\s+/g, "-")}`} className="font-display text-xl font-bold mb-4">{group}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {GAME_CATALOG.filter((g) => g.kind === group).map((g) => (
              <div key={g.slug} id={g.slug} data-testid={`game-entry-${g.slug}`} className="glass rounded-2xl overflow-hidden border border-white/10">
                <img src={`/games/${g.slug}.webp`} alt={g.alt} width="640" height="360" loading="lazy" decoding="async" className="w-full aspect-video object-cover" />
                <div className="p-4">
                  <h3 className="font-semibold text-lg">{g.name}</h3>
                  <p className="text-sm text-gray-400 mt-1 leading-relaxed">{g.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
      <p className="text-sm text-gray-500">
        Ready to play? <Link to="/" data-testid="games-cta-signup" className="text-cyan-400 hover:underline">Create a free account</Link> — you start with a Fun Coin bonus.
      </p>
    </article>
  );
}
